import React, { createContext, Dispatch, useContext, useReducer } from 'react';

import { type DeviceSettings } from '@ledium/types';

import lediumApi from '../../../utils/lediumApi';
import { withShallowBailout } from '../../withShallowBailout';
import { GlobalAction, SET_DATE_TIME_FORMAT, SET_IS_ONLINE, SET_OFFLINE_MODE, SET_SERVER_TIME_OFFSET, useGlobalActions } from '../actions';
import initialGlobalState from '../init';
import { getSocketIo } from '../init/socketIo';
import { globalReducers } from '../reducers';
import { GlobalState } from '../state/initialGlobalState';

interface GlobalStoreContextValue {
    globalState: GlobalState;
    globalDispatch: Dispatch<GlobalAction>;
    globalActions: ReturnType<typeof useGlobalActions>;
}

const GlobalStoreContext = createContext<GlobalStoreContextValue | undefined>(undefined);

function useGlobalStore(): GlobalStoreContextValue {
    const ctx = useContext(GlobalStoreContext);
    if (!ctx) {
        throw new Error('useGlobalStore must be used inside GlobalStoreProvider');
    }
    return ctx;
}

const GlobalStoreProvider = ({ children }: { children: React.ReactNode }) => {
    const reducer = React.useCallback(withShallowBailout(globalReducers), []);
    const [state, dispatch] = useReducer(reducer, initialGlobalState);
    const actions = useGlobalActions(state, dispatch);

    React.useEffect(() => {
        let isMounted = true;
        lediumApi.get<DeviceSettings>('settings/').then((data) => {
            if (isMounted) {
                const clientTime = Date.now();
                dispatch({ type: SET_DATE_TIME_FORMAT, payload: { dateFormat: data?.dateFormat ?? null, timeFormat: data?.timeFormat ?? null } });
                dispatch({ type: SET_OFFLINE_MODE, payload: data?.offlineMode ?? false });
                dispatch({ type: SET_SERVER_TIME_OFFSET, payload: (data?.serverTime ?? clientTime) - clientTime });
            }
        });
        return () => {
            isMounted = false;
        };
    }, []);

    React.useEffect(() => {
        const socket = getSocketIo();
        const onConnectivity = ({ isOnline }: { isOnline: boolean }) => {
            dispatch({ type: SET_IS_ONLINE, payload: isOnline });
        };
        socket.on('connectivity', onConnectivity);
        return () => {
            socket.off('connectivity', onConnectivity);
        };
    }, []);
    const contextValue = React.useMemo(
        () => ({
            globalState: state,
            globalDispatch: dispatch,
            globalActions: actions
        }),
        [state, dispatch, actions]
    );

    return <GlobalStoreContext.Provider value={contextValue}>{children}</GlobalStoreContext.Provider>;
};

export { GlobalStoreContext, useGlobalStore, GlobalStoreProvider };
